import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usersService } from '../services/usersService';
import { patientDocumentsService, patientDocumentTypesService, PatientDocumentRow, PatientDocumentTypeRow } from '../services/patientDocumentsService';
import { useAuth } from '../contexts/AuthContext';
import DocumentModal, { DocumentDraft, CUSTOM_KIND } from './modals/DocumentModal';
import ConfirmModal from './modals/ConfirmModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import { Box, Checkbox, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Delete, Edit, ExpandLess, ExpandMore, Print, UnfoldLess, UnfoldMore } from '@mui/icons-material';
import ClearFiltersButton from './ClearFiltersButton';
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from './Pagination/StandardPagination';
import { colors } from '../theme/designSystem';
import { filterActionButtonSx, dateFieldSx, filterFieldSx, searchFieldSx } from '../theme/filterStyles';
import { DOCUMENTS_PRINT_KEY } from './DocumentsPrint';

/**
 * Aba Documentos do cadastro (RN-539; antes "Receituário" — receita passa a ser um TIPO de
 * documento emitido): mesmo esqueleto das demais abas — filtros reativos, limpar, imprimir e
 * emitir na barra, lista em cartões. Desde 13/09/2026 (item 64) a emissão é REAL: os documentos
 * vivem em `patient_prescriptions` pela API `patient-documents`; os tipos vêm do catálogo da
 * entidade (item 122) e podem trazer um texto padrão que preenche o editor.
 */

const stripHtml = (html: string) => html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ');

const formatDate = (iso: string) => iso.split('-').reverse().join('/');

interface Props {
  patientId: string;
}

const PatientDocumentsTab: React.FC<Props> = ({ patientId }) => {
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [items, setItems] = useState<PatientDocumentRow[]>([]);
  const [types, setTypes] = useState<PatientDocumentTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PatientDocumentRow | null>(null);
  // Acordeão por linha + "abrir todas" (mesmo padrão da aba Evoluções) e seleção para impressão
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const isExpanded = (id: string) => expanded.includes(id);
  const toggleExpanded = (id: string) => setExpanded((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleSelected = (id: string) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const togglePageSelection = () => {
    const ids = paged.map((d) => d.id);
    const all = ids.every((id) => selected.includes(id));
    setSelected((prev) => (all ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]))));
  };
  // Emissor = nome do vínculo do usuário logado (RN-013); cai para o email enquanto carrega
  const [issuerName, setIssuerName] = useState('');
  useEffect(() => {
    usersService.getMyContact().then((c) => setIssuerName(c.fullName || c.email || '')).catch(() => setIssuerName(''));
  }, []);
  const [editing, setEditing] = useState<DocumentDraft | null>(null);

  const load = useCallback(async () => {
    if (!patientId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [docs, activeTypes] = await Promise.all([
        patientDocumentsService.getByPatient(patientId),
        patientDocumentTypesService.getActive().catch(() => [] as PatientDocumentTypeRow[]),
      ]);
      setItems(docs);
      setTypes(activeTypes);
    } catch {
      showToast('Não foi possível carregar os documentos.', 'error');
    } finally {
      setLoading(false);
    }
  }, [patientId, showToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (doc: PatientDocumentRow) => {
    setEditing({ id: doc.id, kind: doc.kind, documentTypeId: doc.documentTypeId, date: doc.date, issuer: doc.issuer, content: doc.content });
    setModalOpen(true);
  };
  const saveDocument = async (draft: DocumentDraft) => {
    setSaving(true);
    try {
      await patientDocumentsService.save(patientId, {
        id: draft.id ?? null,
        kind: draft.kind,
        documentTypeId: draft.documentTypeId,
        date: draft.date,
        content: draft.content,
      });
      showToast(draft.id ? 'Documento atualizado.' : 'Documento emitido.', 'success');
      setModalOpen(false);
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || 'Não foi possível salvar o documento.', 'error');
    } finally {
      setSaving(false);
    }
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await patientDocumentsService.remove(deleteTarget.id);
      showToast('Documento excluído.', 'success');
      setDeleteTarget(null);
      setSelected((prev) => prev.filter((id) => id !== deleteTarget.id));
      await load();
    } catch (e: any) {
      showToast(e?.response?.data?.description || 'Não foi possível excluir o documento.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Filtro por tipo: os nomes que existem na lista (inclui tipos inativados e Personalizado)
  const kindOptions = useMemo(() => Array.from(new Set(items.map((d) => d.kind))).sort((a, b) => a.localeCompare(b)), [items]);

  const normalize = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return items.filter((p) => {
      if (kindFilter && p.kind !== kindFilter) return false;
      if (from && p.date < from) return false;
      if (to && p.date > to) return false;
      if (term) {
        const haystack = normalize(`${p.kind} ${p.issuer} ${stripHtml(p.content)}`);
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [items, search, kindFilter, from, to]);

  const hasFilters = search !== '' || kindFilter !== '' || from !== '' || to !== '';
  const clearFilters = () => { setSearch(''); setKindFilter(''); setFrom(''); setTo(''); setCurrentPage(1); };
  // Impressão no template da clínica: a página de impressão lê a seleção da sessionStorage
  // (o documento já está gravado; a sessão só carrega o que foi escolhido) e o paciente pelo id
  const printDocuments = (docs: PatientDocumentRow[]) => {
    sessionStorage.setItem(DOCUMENTS_PRINT_KEY, JSON.stringify(docs));
    window.open(`?page=documents-print&patientId=${patientId}`, '_blank');
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paged = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const allExpanded = paged.length > 0 && paged.every((d) => expanded.includes(d.id));
  const toggleExpandAll = () => setExpanded(allExpanded ? [] : paged.map((d) => d.id));

  return (
    <div className="tab-content-section">
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Tipo, conteúdo ou emissor"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />
        <TextField
          select
          size="small"
          label="Tipo"
          value={kindFilter}
          onChange={(e) => { setKindFilter(e.target.value); setCurrentPage(1); }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="">Todos</MenuItem>
          {kindOptions.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </TextField>
        <TextField
          size="small"
          type="date"
          label="De"
          value={from}
          onChange={(e) => { setFrom(e.target.value); setCurrentPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          size="small"
          type="date"
          label="Até"
          value={to}
          onChange={(e) => { setTo(e.target.value); setCurrentPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title={filtered.length === 0 ? 'Nenhum documento para imprimir' : selected.length > 0 ? `Imprimir ${selected.length} selecionado(s)` : 'Imprimir todos os documentos filtrados'} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton onClick={() => printDocuments(selected.length > 0 ? filtered.filter((d) => selected.includes(d.id)) : filtered)} disabled={filtered.length === 0} sx={filterActionButtonSx} aria-label="Imprimir documentos">
              <Print />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Emitir documento" arrow>
          <span>
            <IconButton sx={filterActionButtonSx} aria-label="Emitir documento" onClick={openCreate} disabled={!patientId}>
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, px: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{filtered.length}</strong> {filtered.length === 1 ? 'documento encontrado' : 'documentos encontrados'}
        </Typography>
        <Tooltip title={allExpanded ? 'Fechar todos' : 'Abrir todos'} arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton size="small" onClick={toggleExpandAll} disabled={paged.length === 0} sx={{ color: colors.primary }}>
              {allExpanded ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box className="c4u-table c4u-table--serial">
        <Box className="c4u-table__header">
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 44px', justifyContent: 'center' }}>
            <Tooltip title="Selecionar todos desta página para impressão" arrow>
              <Checkbox
                size="small"
                checked={paged.length > 0 && paged.every((d) => selected.includes(d.id))}
                indeterminate={paged.some((d) => selected.includes(d.id)) && !paged.every((d) => selected.includes(d.id))}
                disabled={paged.length === 0}
                onChange={togglePageSelection}
                sx={{ p: 0.25, color: colors.primary, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: colors.primary } }}
              />
            </Tooltip>
          </Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 110px' }}>Data</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '1 1 200px', minWidth: 140 }}>Tipo</Box>
          <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: '1 1 220px', minWidth: 140 }}>Emitido por</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 56px', justifyContent: 'center' }}>Ver</Box>
          <Box className="admin-plans-header-cell" sx={{ flex: '0 0 150px', justifyContent: 'flex-end' }}>Ações</Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: colors.primary }} />
          </Box>
        )}

        {!loading && paged.length === 0 && (
          <Typography sx={{ color: colors.textSecondary, fontSize: '0.875rem', py: 4, textAlign: 'center' }}>
            {items.length === 0 ? 'Nenhum documento emitido para este paciente.' : 'Nenhum documento no filtro.'}
          </Typography>
        )}

        <Box className="admin-plans-table-body">
          {!loading && paged.map((p) => (
            <React.Fragment key={p.id}>
              <Box className="c4u-table__row">
                <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 44px', justifyContent: 'center' }} data-label="Imprimir">
                  <Tooltip title="Selecionar para impressão" arrow>
                    <Checkbox
                      size="small"
                      checked={selected.includes(p.id)}
                      onChange={() => toggleSelected(p.id)}
                      sx={{ p: 0.5, color: colors.primary, '&.Mui-checked': { color: colors.primary } }}
                    />
                  </Tooltip>
                </Box>
                <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 110px' }} data-label="Data">
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatDate(p.date)}</Typography>
                </Box>
                <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 200px', minWidth: 140 }} data-label="Tipo">
                  <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{p.kind === CUSTOM_KIND ? stripHtml(p.content).trim().split(/\s+/).slice(0, 6).join(' ') : p.kind}</Typography>
                </Box>
                <Box className="admin-plans-cell c4u-col--md" sx={{ display: 'flex', alignItems: 'center', flex: '1 1 220px', minWidth: 140 }} data-label="Emitido por">
                  <Typography sx={{ fontSize: 13, overflowWrap: 'anywhere' }}>{p.issuer}</Typography>
                </Box>
                <Box className="admin-plans-cell" sx={{ display: 'flex', alignItems: 'center', flex: '0 0 56px', justifyContent: 'center' }} data-label="Ver">
                  <Tooltip title={isExpanded(p.id) ? 'Fechar conteúdo' : 'Ver conteúdo'} arrow>
                    <IconButton size="small" onClick={() => toggleExpanded(p.id)} sx={{ color: colors.primary }}>
                      {isExpanded(p.id) ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box className="admin-plans-cell admin-plans-actions" sx={{ flex: '0 0 150px' }} data-label="Ações">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                    <Tooltip title="Imprimir este documento" arrow>
                      <IconButton className="c4u-row-action" size="small" onClick={() => printDocuments([p])} sx={{ backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover }, color: colors.white }}>
                        <Print sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    {/* Só quem emitiu edita ou exclui: o documento tem autoria */}
                    <Tooltip title={p.canEdit ? 'Editar' : 'Só quem emitiu pode editar'} arrow>
                      <span>
                        <IconButton className="c4u-row-action" size="small" disabled={!p.canEdit} onClick={() => openEdit(p)} sx={{ backgroundColor: '#6c757d', '&:hover': { backgroundColor: '#5a6268' }, color: colors.white, '&.Mui-disabled': { backgroundColor: '#ced4da', color: colors.white } }}>
                          <Edit sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={p.canEdit ? 'Excluir' : 'Só quem emitiu pode excluir'} arrow>
                      <span>
                        <IconButton className="c4u-row-action" size="small" disabled={!p.canEdit} onClick={() => setDeleteTarget(p)} sx={{ backgroundColor: '#dc3545', '&:hover': { backgroundColor: '#c82333' }, color: colors.white, '&.Mui-disabled': { backgroundColor: '#ced4da', color: colors.white } }}>
                          <Delete sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              {isExpanded(p.id) && (
                <Box className="c4u-table__expand" sx={{ px: { xs: 1.5, md: 3 }, py: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e2e8f0', mb: { xs: 1.5, md: 0 } }}>
                  <Box
                    sx={{ fontSize: '0.875rem', color: colors.textPrimary, lineHeight: 1.6, '& p': { m: 0, mb: 0.5 }, '& ul, & ol': { my: 0.5, pl: 3 } }}
                    dangerouslySetInnerHTML={{ __html: p.content }}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {filtered.length > 0 && (
        <StandardPagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={filtered.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
        />
      )}
      <DocumentModal
        open={modalOpen}
        patientId={patientId}
        initial={editing}
        defaultIssuer={issuerName || user?.email || ''}
        types={types}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSave={saveDocument}
      />
      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir documento"
        message={`Excluir o documento "${deleteTarget?.kind ?? ''}" de ${deleteTarget ? formatDate(deleteTarget.date) : ''}? Ele sai da lista e da impressão.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientDocumentsTab;
